import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useSocialShareAccessDeniedStep from 'containers/SocialShareModal/SocialShareAccessDeniedStep';
import { useSocialShareModal } from 'containers/SocialShareModal/SocialShareModalContext';
import useOnShare from 'containers/SocialShareModal/useOnShare';
import useBlueskyAuthenticator from 'hooks/useBlueskyAuthenticator';
import {
  scheduleBlueskyVideo,
  shareBlueskyVideo,
} from 'redux/modules/download';
import { blueskySelector } from 'redux/modules/social';
import { Dispatch } from 'redux/types';
import { SocialSharePlatformData } from '../types';
import { useBlueskyPostComposerStep } from './steps';
import { BlueskyShareVideoOptions, BlueskySocialShareStep } from './types';

export default function useBlueskySocialShare(): SocialSharePlatformData<
  BlueskySocialShareStep
> {
  const {
    providerUserId,
    profileImageUrl,
    username,
    isLoadingUser,
    postStatus,
  } = useSelector(blueskySelector);

  const dispatch = useDispatch<Dispatch>();

  const {
    description,
    onAuthSuccess,
    onAuthError,
    goToStep,
  } = useSocialShareModal();

  const { authenticating, authenticate } = useBlueskyAuthenticator({
    force: true,
    appRedirectUrl: window.location.href,
    onAuthSuccess: () => {
      onAuthSuccess();
      goToStep('composer');
    },
    onError: error => {
      onAuthError(error);
      goToStep('access-denied');
    },
  });

  const handleShare = useOnShare<[BlueskyShareVideoOptions]>({
    shareNow: useCallback(
      ({ allowQuotePost, replyLevel, customReplyAudiences }) => {
        dispatch(
          shareBlueskyVideo(
            allowQuotePost,
            replyLevel,
            customReplyAudiences,
            description,
          ),
        );
      },
      [description, dispatch],
    ),
    schedule: useCallback(
      (
        scheduledDate: number,
        { allowQuotePost, replyLevel, customReplyAudiences },
      ) =>
        dispatch(
          scheduleBlueskyVideo(
            allowQuotePost,
            replyLevel,
            customReplyAudiences,
            description,
            scheduledDate,
          ),
        ),
      [description, dispatch],
    ),
  });

  const blueskyAccessDeniedStep = useSocialShareAccessDeniedStep({
    onCancel: () => goToStep('composer'),
  });

  const blueskyPostComposerStep = useBlueskyPostComposerStep({
    onShare: handleShare,
  });

  return {
    platformAlias: 'bluesky',
    authenticating: authenticating || isLoadingUser || !username,
    accounts: [
      {
        id: providerUserId,
        avatarSrc: profileImageUrl,
        username,
        handle: username,
      },
    ],
    steps: [blueskyAccessDeniedStep, blueskyPostComposerStep],
    defaultStep: blueskyPostComposerStep.id,
    postStatus,
    authenticate,
  };
}
